// Begründungsfrage — eine Rückfrage, die eine getippte Begründung VERLANGT, bevor sie bestätigt.
//
// Anlass: das Ablehnen eines Inhalts. Das Backend nimmt die Begründung zwar entgegen
// (`ContentController.rejectContent` legt sie über `contentMapping` in `metadata.rejectionReason`
// ab), VERLANGT sie aber nicht — ohne `reason` im Rumpf wird kommentarlos abgelehnt. Wer später
// wissen will, warum ein Beitrag abgelehnt wurde, findet dann nichts. Die Pflicht muss also hier
// sitzen, an der einzigen Stelle, an der die Ablehnung ausgelöst werden kann.
//
// ── ⚠️ Warum NICHT die `Rueckfrage` erweitert wurde ─────────────────────────────────────────────
// Die naheliegende Alternative war, `RückfrageWunsch` um ein Eingabefeld zu ergänzen. Dagegen
// spricht nicht Geschmack, sondern ihr Vertrag:
//
//   1. `useRueckfrage.frage()` gibt ein `Promise<boolean>` zurück und `antworte(bestätigt: boolean)`
//      löst es ein. Ein Text passt in keines von beidem. Ihn mitzuführen hieße, beide Signaturen zu
//      verbreitern — für ALLE bisherigen Aufrufstellen (siebzehn umgestellte `window.confirm()`),
//      von denen keine einzige einen Text braucht. Eine Zusage „gib mir true/false" würde damit zu
//      „gib mir true/false ODER einen String", und jede Aufrufstelle müsste den Fall behandeln, den
//      sie nie stellt.
//   2. Die Anwendung hat diese Frage schon einmal entschieden, und zwar gegen die Erweiterung:
//      `components/admin/BunnyCDNManager.tsx` hält für seine drei FORMULAR-Dialoge eine eigene
//      Hülle, ausdrücklich mit der Begründung „die `Rueckfrage` kennt nur Titel, Text und zwei
//      Knöpfe; sie könnte keinen von ihnen ersetzen". Geteilt wird dort das, woran es fehlt —
//      `useFokusfang`. Genau dieser Linie folgt dieser Baustein.
//   3. Der voreingestellte Fokus ist ein ANDERER. Die `Rueckfrage` setzt ihn ausdrücklich auf
//      „Abbrechen", damit ein reflexhaftes Enter nichts löscht. Hier gehört er ins Eingabefeld:
//      ohne Text kann der Knopf ohnehin nichts auslösen (siehe `absenden` unten), und Enter im
//      Textfeld erzeugt eine Zeile statt einer Ablehnung.
//
// Nachgebaut wird deshalb NICHTS: Schleier und Portal kommen aus `ModalSchicht`, Fokusfang,
// Escape und Fokus-Rückkehr aus `useFokusfang`. Eigen ist allein das Feld dazwischen samt seiner
// Pflicht.
import React, { useId, useRef, useState } from 'react';
import { useFokusfang } from '../../hooks/useFokusfang';
import { ModalSchicht } from './ModalSchicht';

/**
 * Stapelhöhe des Schleiers — dieselbe wie die der `Rueckfrage`, und aus denselben zwei Gründen
 * (dort ausführlich an `RÜCKFRAGE_STAPEL` hergeleitet, hier nur die Kurzform):
 *
 * - ÜBER `z-50`, denn das tragen alle Overlays der Anwendung (`ModalSchicht`-Vorgabe,
 *   `.modal-overlay` und `.bunny-file-picker-overlay` in `index.css`, der Titel-Wähler der
 *   Playlists). Bei Gleichstand entschiede die Montage-Reihenfolge — eine Sicherheitsabfrage, die
 *   zufällig sichtbar ist, ist keine.
 * - UNTER `z-[60]`, dem schwebenden Benachrichtigungs-Balken. Sonst verschwände eine stehende
 *   Fehlermeldung hinter dem Schleier, sobald dieser Dialog aufgeht.
 *
 * ⚠️ Bewusst eine eigene Konstante statt eines Imports aus `Rueckfrage.tsx`: die ist dort
 * modul-intern, und `eslint-plugin-react-refresh` verbietet einer Datei mit Komponenten-Export
 * zusätzliche Wert-Exporte. Ein gemeinsames Modul für einen einzigen Klassennamen wäre mehr
 * Apparat als Nutzen; die Kopplung ist dafür hier benannt.
 */
const BEGRÜNDUNG_STAPEL = 'z-[55]';

export interface BegründungsfrageProps {
  /** Überschrift des Dialogs. Wird als Name des Dialogs angesagt (`aria-labelledby`). */
  titel: string;
  /** Der erklärende Absatz darüber. Zeilenumbrüche bleiben erhalten (`whitespace-pre-line`). */
  text: string;
  /** Beschriftung des Textfeldes. Trägt das `htmlFor`, ist also der angesagte Name des Feldes. */
  feldBeschriftung: string;
  /** Was am Feld steht, solange nichts getippt wurde. Kein Ersatz für die Beschriftung. */
  platzhalter?: string;
  /** Beschriftung des bestätigenden Knopfes. */
  bestätigenText: string;
  /** Abbruch — Knopf UND Escape. Beide Wege sind ausdrücklich derselbe. */
  onAbbrechen: () => void;
  /** Bestätigung. Bekommt die Begründung ohne umgebende Leerzeichen, garantiert nicht leer. */
  onBestätigen: (begründung: string) => void;
}

export const Begründungsfrage: React.FC<BegründungsfrageProps> = ({
  titel,
  text,
  feldBeschriftung,
  platzhalter,
  bestätigenText,
  onAbbrechen,
  onBestätigen,
}) => {
  const titelId = useId();
  const textId = useId();
  const feldId = useId();
  const hinweisId = useId();
  const feldRef = useRef<HTMLTextAreaElement>(null);
  const [begründung, setBegründung] = useState('');
  const [fehlt, setFehlt] = useState(false);

  const flächeRef = useFokusfang<HTMLDivElement>({
    beiEscape: onAbbrechen,
    anfangsfokus: feldRef,
  });

  /**
   * ⚠️ Der Knopf ist NICHT gesperrt, solange das Feld leer ist — und das ist Absicht. Ein
   * gesperrter Knopf sagt nicht, WARUM er nicht geht: er ist für Vorlesehilfen übersprungen, trägt
   * keine Begründung und lässt die Nutzerin raten, ob sie etwas falsch gemacht hat oder die Seite
   * hängt. Stattdessen antwortet der Klick — mit einem Hinweis, der als `role="alert"` angesagt
   * wird, und mit dem Fokus zurück im Feld.
   *
   * `trim()`, damit drei Leerzeichen nicht als Begründung durchgehen; weitergereicht wird der
   * getrimmte Text, nicht der rohe.
   */
  const absenden = () => {
    const getrimmt = begründung.trim();
    if (!getrimmt) {
      setFehlt(true);
      feldRef.current?.focus();
      return;
    }
    onBestätigen(getrimmt);
  };

  return (
    <ModalSchicht stapelKlasse={BEGRÜNDUNG_STAPEL}>
      <div
        ref={flächeRef}
        // `dialog`, nicht `alertdialog`: die dringende Rolle bleibt der `Rueckfrage` vorbehalten,
        // deren ganzer Inhalt die Meldung IST. Hier ist der Text eine Anweisung an das Feld
        // darunter, und die Antwort wird getippt, nicht gewählt. Dieselbe Abgrenzung trifft
        // `MediathekDialog` in `components/admin/BunnyCDNManager.tsx`.
        role="dialog"
        aria-modal="true"
        aria-labelledby={titelId}
        aria-describedby={textId}
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
      >
        <h2 id={titelId} className="text-xl font-bold text-secondary-900">
          {titel}
        </h2>
        <p id={textId} className="mt-3 text-sm text-secondary-700 whitespace-pre-line">
          {text}
        </p>

        {/* `useId` statt einer festen Kennung: der Dialog kann neben einem offenen Formular stehen,
            und eine doppelte `id` bricht `htmlFor` STILL — der Verweis fände das erste Vorkommen im
            Dokument, also das fremde Feld. Begründung im Kopf von `ui/Filterleiste.tsx`. */}
        <label htmlFor={feldId} className="mt-5 block text-sm font-medium text-secondary-700 mb-2">
          {feldBeschriftung}
        </label>
        <textarea
          id={feldId}
          ref={feldRef}
          value={begründung}
          onChange={(e) => {
            setBegründung(e.target.value);
            // Der Hinweis verschwindet beim ersten Tastendruck. Stehen zu bleiben, während die
            // Nutzerin gerade das Verlangte tut, wäre eine Meldung, die ihrer eigenen Anzeige
            // widerspricht.
            if (fehlt) setFehlt(false);
          }}
          rows={4}
          className="input w-full"
          placeholder={platzhalter}
          // `aria-invalid` nur im Fehlerfall, und `undefined` statt `false`: ein Feld, das
          // dauerhaft `aria-invalid="false"` trägt, ist zwar korrekt, aber die Umschaltung ist
          // das, was Vorlesehilfen melden.
          aria-invalid={fehlt || undefined}
          aria-describedby={fehlt ? hinweisId : undefined}
        />
        {fehlt && (
          <p id={hinweisId} role="alert" className="mt-2 text-sm text-error-600">
            Bitte eine Begründung eintragen.
          </p>
        )}

        <div className="mt-6 flex items-center justify-end gap-3">
          {/* Abbrechen links, wie in der `Rueckfrage`. */}
          <button
            type="button"
            onClick={onAbbrechen}
            className="btn btn-outline cursor-pointer"
          >
            Abbrechen
          </button>
          <button type="button" onClick={absenden} className="btn btn-primary cursor-pointer">
            {bestätigenText}
          </button>
        </div>
      </div>
    </ModalSchicht>
  );
};
